import { faSpinnerThird as fadSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';

import { DrawerBody, Icon } from '@growtherapy/sprout-ui';

import {
  ClientInformationDrawerTab,
  INTAKE_SUMMARY_SOURCE,
  IntakeSummaryConnected,
  useMarkFormResponseAsReviewed,
  useSetActiveClientInfoDrawerTab,
  useTrackIntakeFormReviewDuration,
} from '@grow-therapy-team/seedling-components';

import { ProviderDrawerState, drawerStateAtom } from '../state';
import { ClientInformationDrawerHeader } from './ClientInformationDrawerHeader';
import { FormResponse } from './FormResponse';
import { FormListItem } from './form-list-item';
import {
  formDataForSelectedClientAtom,
  intakeFormReviewSourceAtom,
  selectedFormTitleAtom,
  selectedGroupedFormAtom,
} from './state';
import { FormType, GroupedFormData } from './types';
import { useSelectedClientAppointmentIds } from './useSelectedClientAppointmentIds';

function useIntakeFormReviewTracking(
  selectedForms: GroupedFormData[] | undefined,
  formEntry: number,
  revealResults?: boolean,
) {
  const { markFormResponseAsReviewed } = useMarkFormResponseAsReviewed();
  const markedResponseIds = useRef<Set<string>>(new Set());

  useEffect(
    function markIntakeFormAsReviewedOnView() {
      const selectedForm = selectedForms?.[formEntry];
      const responseId = selectedForm?.formResponseId;
      if (!revealResults || selectedForm?.formType !== FormType.Intake || !responseId) return;
      if (markedResponseIds.current.has(responseId)) return;
      markedResponseIds.current.add(responseId);
      markFormResponseAsReviewed({ formResponseId: parseInt(responseId) });
    },
    [revealResults, formEntry, selectedForms, markFormResponseAsReviewed],
  );

  const source = useAtomValue(intakeFormReviewSourceAtom);
  const selectedForm = selectedForms?.[formEntry];
  const responseId = selectedForm?.formResponseId;

  // Duration comes from the shared seedlings hook so telehealth and csr-app report the
  // same thing: on-screen time, excluding stretches where the tab was hidden.
  useTrackIntakeFormReviewDuration({
    isActive: Boolean(revealResults) && selectedForm?.formType === FormType.Intake,
    formResponseId: responseId ? parseInt(responseId) : undefined,
    source,
  });
}

type ClientInformationFormResponseDrawerProps = {
  selectedForms?: GroupedFormData[];
  revealResults?: boolean;
  selectedFormTitle: string;
  onClickForm?: (i: number) => void;
  onClickBackButton: () => void;
  formEntry?: number;
  appointmentShortId?: string;
  isLoading?: boolean;
};

export function ClientInformationFormResponseDrawer({
  selectedForms,
  selectedFormTitle,
  formEntry = 0,
  onClickBackButton,
  onClickForm,
  revealResults,
  appointmentShortId,
  isLoading,
}: ClientInformationFormResponseDrawerProps) {
  if (isLoading) {
    return (
      <>
        <ClientInformationDrawerHeader drawerTitle={selectedFormTitle} onClickBackButton={onClickBackButton} />
        <DrawerBody>
          <div className='flex justify-center'>
            <Icon
              aria-hidden={false}
              aria-label='Loading...'
              size='xl'
              data-testid='client-information.forms.loading'
              className='text-ivy-500 fa-spin'
              icon={fadSpinnerThird}
            />
          </div>
        </DrawerBody>
      </>
    );
  }

  if (!selectedForms) return null;

  const drawerTitle =
    selectedForms[0].formType === FormType.Gad7 || selectedForms[0].formType === FormType.Phq9
      ? `${selectedForms[0].formType} - ${selectedFormTitle}`
      : selectedFormTitle;

  const selectedForm = selectedForms[formEntry];
  const showIntakeSummary = revealResults && selectedForm?.formType === FormType.Intake && !!appointmentShortId;

  return (
    <>
      <ClientInformationDrawerHeader drawerTitle={drawerTitle} onClickBackButton={onClickBackButton} />
      <DrawerBody>
        {!revealResults ? (
          <span data-testid='client-information.forms.form-list'>
            {selectedForms.map((form, i) => (
              <FormListItem
                form={form}
                key={`${form.formType}-${i}`}
                onClick={() => {
                  onClickForm?.(i);
                }}
              />
            ))}
          </span>
        ) : (
          <>
            {showIntakeSummary && (
              <div className='mb-4'>
                <IntakeSummaryConnected
                  appointmentShortId={appointmentShortId}
                  showViewFullIntakeButton={false}
                  shouldTruncateSummaryText={false}
                  source={INTAKE_SUMMARY_SOURCE.FORM_RESPONSE}
                  isInverse
                />
              </div>
            )}
            <FormResponse selectedForm={selectedForm} />
          </>
        )}
      </DrawerBody>
    </>
  );
}

export function ClientInformationFormResponseDrawerWrapper() {
  const selectedFormTitle = useAtomValue(selectedFormTitleAtom);
  const selectedFormResponses = useAtomValue(selectedGroupedFormAtom);
  const formDataForSelectedClient = useAtomValue(formDataForSelectedClientAtom);
  const hasSingleSubmission = selectedFormResponses?.length === 1;
  const setDrawerState = useSetAtom(drawerStateAtom);
  const setIntakeFormReviewSource = useSetAtom(intakeFormReviewSourceAtom);
  const setSeedlingActiveTab = useSetActiveClientInfoDrawerTab();
  // null until the user toggles; falls back to auto-revealing a single submission once forms load
  const [userToggledRevealResults, setUserToggledRevealResults] = useState<boolean | null>(null);
  const revealResults = userToggledRevealResults ?? Boolean(hasSingleSubmission);
  const [formEntry, setFormEntry] = useState(0);
  const { appointmentShortId } = useSelectedClientAppointmentIds();

  useIntakeFormReviewTracking(selectedFormResponses, formEntry, revealResults);

  const onClickBackButton = () => {
    if (revealResults && !hasSingleSubmission) {
      setUserToggledRevealResults(false);
    } else {
      setSeedlingActiveTab(ClientInformationDrawerTab.INFORMATION);
      setDrawerState(ProviderDrawerState.CLIENT_INFORMATION);
    }
  };

  const onClickForm = (i: number) => {
    // Picking a response off the list is its own entry point, even when a summary
    // click-through is what opened the drawer.
    setIntakeFormReviewSource('telehealth_forms_list');
    setFormEntry(i);
    setUserToggledRevealResults(true);
  };

  if (!selectedFormTitle) return;

  const isLoadingForms = !formDataForSelectedClient;

  if (isLoadingForms) {
    return (
      <ClientInformationFormResponseDrawer
        selectedFormTitle={selectedFormTitle}
        isLoading
        onClickBackButton={onClickBackButton}
      />
    );
  }

  if (!selectedFormResponses) return;

  return (
    <ClientInformationFormResponseDrawer
      selectedFormTitle={selectedFormTitle}
      revealResults={revealResults}
      selectedForms={selectedFormResponses}
      onClickBackButton={onClickBackButton}
      onClickForm={onClickForm}
      formEntry={formEntry}
      appointmentShortId={appointmentShortId}
    />
  );
}
